/**
 * The iOS-style switch that enables an outcome, a reel, a fixture
 * (design-system.md §6 "Toggle"; `.m-toggle` in the prototype's modals.css).
 *
 * A real `<input type="checkbox">` behind a visually-hidden style, with the
 * track and thumb drawn over it. That is what the prototype does, and it is what
 * keeps Space working, the label clickable, and the state announced — none of
 * which a styled `<div>` with an onClick would give.
 *
 * > **30×18, not the 32×18 §6 and the ticket both quote.** `.m-toggle-track` is
 * > 30 wide and the thumb travels to `left: 14px`, which only lands flush inside
 * > 30 (2 + 14 + 14 = 30). At 32 the thumb would stop 2px short of the right
 * > inset it sits at on the left. The stylesheet is self-consistent and the
 * > prose is not, so the stylesheet wins.
 */
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';

const TRACK_WIDTH = 30;
const TRACK_HEIGHT = 18;
const THUMB = 14;
const INSET = 2;

interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Always required — it is the switch's accessible name. */
  label: string;
  /**
   * Draw the switch alone. The label still names it for assistive tech; use this
   * where the surrounding row already says in words what is being toggled.
   */
  hideLabel?: boolean;
  disabled?: boolean;
}

export const Toggle = ({ checked, onChange, label, hideLabel, disabled }: ToggleProps) => (
  <Box
    component="label"
    sx={(theme: Theme) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      fontSize: '13px',
      fontWeight: 500,
      color: theme.tokens.color.ink2,
    })}
  >
    <Box
      component="input"
      type="checkbox"
      checked={checked}
      disabled={disabled}
      aria-label={hideLabel ? label : undefined}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
      sx={{
        // Off-screen rather than `display: none` or `visibility: hidden`, both of
        // which take the input out of the accessibility tree and the tab order
        // along with it.
        position: 'absolute',
        opacity: 0,
        width: 0,
        height: 0,
        margin: 0,
      }}
    />
    <Box
      aria-hidden
      component="span"
      sx={(theme: Theme) => ({
        position: 'relative',
        flexShrink: 0,
        width: `${TRACK_WIDTH}px`,
        height: `${TRACK_HEIGHT}px`,
        borderRadius: `${theme.tokens.radius.pill}px`,
        backgroundColor: checked
          ? theme.tokens.color.accent
          : theme.tokens.componentColor.toggleTrackOff,
        transition: 'background 0.15s',
        // The focus ring lives here because the input it belongs to is invisible.
        'input:focus-visible + &': { boxShadow: theme.tokens.shadow.currentStepRing },
      })}
    >
      <Box
        component="span"
        sx={(theme: Theme) => ({
          position: 'absolute',
          top: `${INSET}px`,
          left: `${checked ? TRACK_WIDTH - THUMB - INSET : INSET}px`,
          width: `${THUMB}px`,
          height: `${THUMB}px`,
          borderRadius: '50%',
          backgroundColor: theme.tokens.color.surface,
          boxShadow: theme.tokens.shadow.toggleThumb,
          transition: 'left 0.15s',
        })}
      />
    </Box>
    {hideLabel ? null : label}
  </Box>
);
